<template>
  <section class="vf-o-tab-menu">
    <div class="tab-menu">
      <div
        class="tab-menu__scroller"
        :class="{
          'tab-menu__scroller--start': !atStart,
          'tab-menu__scroller--end': !atEnd,
        }"
      >
        <ul
          ref="scrollRef"
          class="tab-menu__list flex justify-start w-full border-b md:overflow-hidden overflow-x-scroll overflow-y-hidden"
          role="tablist"
          aria-label="Product collections"
          @scroll.passive="handleScroll"
        >
          <li
            v-for="(tab, index) in tabs"
            :key="index"
            :ref="(el) => setTabRef(el, index)"
            role="presentation"
            class="-mb-px mr-1 pb-0.5 flex shrink-0"
          >
            <button
              type="button"
              role="tab"
              :aria-selected="selectedTab === index"
              :tabindex="selectedTab === index ? 0 : -1"
              @click="selectTab(index)"
              :class="{
                'inline-block py-3 px-4 bg-transparent cursor-pointer text-customBlack-500 border-b border-customBlack-500':
                  selectedTab === index,
                'inline-block py-3 px-4 bg-transparent cursor-pointer text-customBlack-500 hover:text-customBlack-500 hover:border-customBlack-500':
                  selectedTab !== index,
              }"
            >
              {{ tab?.name }}
            </button>
          </li>
        </ul>

        <!-- Right-edge chevron hint: only while there's more to the right and
             before the user has scrolled. Fades out after first interaction. -->
        <span
          class="tab-menu__hint"
          :class="{ 'tab-menu__hint--visible': !atEnd && !hasScrolled }"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M9 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </div>
      <div class="tab-content mt-5">
        <!-- Default slot owns its content and receives the active index, so the
             parent can keep all panels mounted (no remount/flash on switch).
             Falls back to the legacy per-tab named slots if provided. -->
        <slot :selectedTab="selectedTab">
          <slot :name="`tab-${selectedTab}`"></slot>
        </slot>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
  tabs: {
    type: Array<any>,
    required: true,
  },
  selectedTab: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["update:selectedTab"]);

const selectedTab = ref(props.selectedTab);

// --- Scroll-affordance state -----------------------------------------------
const scrollRef = ref<HTMLElement | null>(null);
const tabEls = ref<HTMLElement[]>([]);
const atStart = ref(true);
const atEnd = ref(true);
const hasScrolled = ref(false);

const setTabRef = (el: any, index: number) => {
  if (el) tabEls.value[index] = el as HTMLElement;
};

const updateEdges = () => {
  const node = scrollRef.value;
  if (!node) return;
  const max = node.scrollWidth - node.clientWidth;
  atStart.value = node.scrollLeft <= 1;
  atEnd.value = node.scrollLeft >= max - 1;
};

const handleScroll = () => {
  hasScrolled.value = true;
  updateEdges();
};

// Bring the active tab into view (centred) so it's never hidden off-screen.
const scrollActiveIntoView = (smooth = true) => {
  const node = scrollRef.value;
  const el = tabEls.value[selectedTab.value];
  if (!node || !el) return;
  const target = el.offsetLeft - node.clientWidth / 2 + el.offsetWidth / 2;
  node.scrollTo({
    left: Math.max(0, target),
    behavior: smooth ? "smooth" : "auto",
  });
};

const selectTab = (index: number) => {
  selectedTab.value = index;
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  nextTick(() => {
    updateEdges();
    scrollActiveIntoView(false);
  });
  if (typeof ResizeObserver !== "undefined" && scrollRef.value) {
    resizeObserver = new ResizeObserver(() => updateEdges());
    resizeObserver.observe(scrollRef.value);
  }
  window.addEventListener("resize", updateEdges, { passive: true });
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  window.removeEventListener("resize", updateEdges);
});

watch(
  () => props.selectedTab,
  (newIndex) => {
    selectedTab.value = newIndex;
  }
);

watch(selectedTab, (newIndex) => {
  emit("update:selectedTab", newIndex);
  nextTick(() => scrollActiveIntoView());
});
</script>

<style scoped>
.tab-menu__scroller {
  position: relative;
}

.tab-menu__list {
  scrollbar-color: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* Hide the native scrollbar on mobile — the edge fade is the affordance. */
@media (max-width: 767px) {
  .tab-menu__list {
    /* Trailing space so the final tab is never flush to the edge → it peeks,
       signalling there's more to scroll. */
    /* Trailing space so the final tab is never flush to the edge → it peeks,
       signalling there's more to scroll. */
    padding-right: 4.5rem;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .tab-menu__list::-webkit-scrollbar {
    display: none;
  }

  /* A touch more breathing room so the five tabs reliably overflow the
     viewport, guaranteeing a partial tab peeks past the edge. */
  .tab-menu__list > li > a {
    padding-left: 1.15rem;
    padding-right: 1.15rem;
  }

  /* Wider right fade to match the larger peek. */
  .tab-menu__scroller::after {
    width: 4rem;
  }
}

/* --- Edge fade masks: only when there's content hidden that way --------- */
.tab-menu__scroller::before,
.tab-menu__scroller::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2.5rem;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.25s ease;
  z-index: 2;
}

.tab-menu__scroller::before {
  left: 0;
  background: linear-gradient(
    to right,
    var(--vf-tab-fade-color, #ffffff),
    rgba(255, 255, 255, 0)
  );
}

.tab-menu__scroller::after {
  right: 0;
  background: linear-gradient(
    to left,
    var(--vf-tab-fade-color, #ffffff),
    rgba(255, 255, 255, 0)
  );
}

.tab-menu__scroller--start::before {
  opacity: 1;
}

.tab-menu__scroller--end::after {
  opacity: 1;
}

/* --- Right-edge chevron hint -------------------------------------------- */
.tab-menu__hint {
  position: absolute;
  right: 0.35rem;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--customBlack-500, #101820);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-50%);
  transition: opacity 0.3s ease;
  z-index: 3;
  animation: tabHintNudge 1.6s ease-in-out infinite;
}

.tab-menu__hint--visible {
  opacity: 0.55;
}

@keyframes tabHintNudge {
  0%,
  100% {
    margin-right: 0;
  }
  50% {
    margin-right: 5px;
  }
}

/* The edge affordances are a mobile concern; on md+ everything fits. */
@media (min-width: 768px) {
  .tab-menu__scroller::before,
  .tab-menu__scroller::after,
  .tab-menu__hint {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tab-menu__hint {
    animation: none;
  }
  .tab-menu__list {
    scroll-behavior: auto;
  }
}

/* Smooth underline + colour shift when switching tabs */
.tab-menu li a {
  transition: color 0.25s ease, border-color 0.25s ease, opacity 0.25s ease;
  white-space: nowrap;
}
</style>
